import { Layers,Star, Wrench } from 'lucide-react';
import React from 'react';

import { SearchBar } from '@/components/common/SearchBar';
import { Tabs, TabsContent,TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';
import type { FavoriteItem } from '@/types/toolBrowser';

import { FavoritesTab } from './FavoritesTab';
import { PipelinesTab } from './PipelinesTab';
import { ToolsTab } from './ToolsTab';
import { ViewModeToggle } from './ViewModeToggle';

export interface TabToolbarProps {
  onSelectTool: (toolId: string) => void;
  onAddToolToPipeline: (toolId: string) => void;
  filteredFavoriteItems: FavoriteItem[];
  selectedToolId: string | null;
  selectedPresetId: string | null;
  recommendationMap: Map<string, { score: number; reason: string }>;
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  onSelectPreset: (presetId: string, toolId: string) => void;
  onAddPresetToPipeline: (presetId: string, toolId: string, config: Record<string, unknown>, name: string) => void;
  sortedPipelines: ISavedPipeline[];
  onOpenPipeline: (pipeline: ISavedPipeline) => void;
  onEditPipeline: (pipeline: ISavedPipeline) => void;
  onDeletePipeline: (pipeline: ISavedPipeline) => void;
}

export const TabToolbar: React.FC<TabToolbarProps> = (props) => {
  const mode = useToolBrowserStore((s) => s.mode);
  const setMode = useToolBrowserStore((s) => s.setMode);
  const query = useToolBrowserStore((s) => s.query);
  const setQuery = useToolBrowserStore((s) => s.setQuery);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Tabs
        value={mode}
        onValueChange={(v) => setMode(v as 'all' | 'favorites' | 'pipelines')}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex items-center justify-between border-b border-border shrink-0">
          <TabsList className="border-b-0 w-full">
            <TabsTrigger value="all" className="gap-1">
              <Wrench className="h-3.5 w-3.5" /> Tools
            </TabsTrigger>
            <TabsTrigger value="favorites" className="gap-1">
              <Star className="h-3.5 w-3.5" /> Favorites
              {props.filteredFavoriteItems.length > 0 && (
                <span className="ml-0.5 rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">{props.filteredFavoriteItems.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pipelines" className="gap-1">
              <Layers className="h-3.5 w-3.5" /> Pipelines
              {props.sortedPipelines.length > 0 && (
                <span className="ml-0.5 rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">{props.sortedPipelines.length}</span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {mode !== 'pipelines' && (
          <SearchBar query={query} onQueryChange={setQuery} placeholder={mode === 'favorites' ? 'Search favorites…' : 'Search tools…'} />
        )}
        {mode === 'all' && (
          <div className="flex items-center justify-end px-3 pb-1 shrink-0"><ViewModeToggle /></div>
        )}

        <TabsContent value="all" className="flex-1 min-h-0">
          <ToolsTab onSelectTool={props.onSelectTool} onAddToPipeline={props.onAddToolToPipeline} />
        </TabsContent>
        <TabsContent value="favorites" className="flex-1 min-h-0">
          <FavoritesTab
            filteredFavoriteItems={props.filteredFavoriteItems}
            selectedToolId={props.selectedToolId}
            selectedPresetId={props.selectedPresetId}
            recommendationMap={props.recommendationMap}
            isFavorite={props.isFavorite}
            onToggleFavorite={props.onToggleFavorite}
            onSelectTool={props.onSelectTool}
            onSelectPreset={props.onSelectPreset}
            onAddToolToPipeline={props.onAddToolToPipeline}
            onAddPresetToPipeline={props.onAddPresetToPipeline}
          />
        </TabsContent>
        <TabsContent value="pipelines" className="flex-1 min-h-0">
          <PipelinesTab
            sortedPipelines={props.sortedPipelines}
            onOpenPipeline={props.onOpenPipeline}
            onEditPipeline={props.onEditPipeline}
            onDeletePipeline={props.onDeletePipeline}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};